import type { eventWithTime } from '@rrweb/types';
import type { RrwebChunksSchema } from '../schemas/rrweb';
import { db } from '../db';

const MAX_CHUNKS = 12;
const MAX_TOTAL_SIZE_BYTES = 50 * 1024 * 1024;
const TAB_ID_KEY = 'rrwebTabId';

export function getOrCreateTabId(): string {
  let id = sessionStorage.getItem(TAB_ID_KEY);

  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(TAB_ID_KEY, id);
  }

  return id;
}

export function listChunks(tabId?: string): Promise<RrwebChunksSchema[]> {
  if (tabId) {
    return db.rrwebChunks.where('tabId').equals(tabId).sortBy('startTime');
  }

  return db.rrwebChunks.orderBy('startTime').toArray();
}

export async function getChunkEvents(
  chunkId: string,
): Promise<eventWithTime[]> {
  const rows = await db.rrwebEvents
    .where('chunkId')
    .equals(chunkId)
    .sortBy('timestamp');

  return rows.map((row) => row.data as eventWithTime);
}

export async function getChunkNetwork(chunkId: string): Promise<unknown[]> {
  const chunk = await db.rrwebChunks.get(chunkId);

  if (!chunk?.networkJson) return [];

  return JSON.parse(chunk.networkJson) as unknown[];
}

export function findOpenChunk(
  tabId: string,
): Promise<RrwebChunksSchema | undefined> {
  return db.rrwebChunks.where({ tabId, status: 'open' as const }).first();
}

export function createOpenChunk(
  meta: Pick<RrwebChunksSchema, 'tabId' | 'startTime'>,
): Promise<string> {
  const id = crypto.randomUUID();

  return db.rrwebChunks
    .add({
      id,
      tabId: meta.tabId,
      startTime: meta.startTime,
      endTime: meta.startTime,
      eventCount: 0,
      sizeEstimate: 0,
      status: 'open',
      elapsedMs: 0,
    })
    .then(() => id);
}

export async function appendToChunk(
  chunkId: string,
  events: eventWithTime[],
  networkCalls: unknown[],
  meta: { elapsedMs: number },
): Promise<void> {
  await db.transaction('rw', db.rrwebEvents, db.rrwebChunks, async () => {
    const chunk = await db.rrwebChunks.get(chunkId);

    if (!chunk) throw new Error(`rrweb chunk not found: ${chunkId}`);

    const existingNetwork: unknown[] = chunk.networkJson
      ? (JSON.parse(chunk.networkJson) as unknown[])
      : [];

    existingNetwork.push(...networkCalls);

    await db.rrwebEvents.bulkAdd(
      events.map((data) => ({
        chunkId,
        timestamp: data.timestamp,
        data,
      })),
    );

    const addedSize =
      JSON.stringify(events).length + JSON.stringify(networkCalls).length;

    await db.rrwebChunks.update(chunkId, {
      eventCount: chunk.eventCount + events.length,
      sizeEstimate: chunk.sizeEstimate + addedSize,
      endTime: Date.now(),
      elapsedMs: meta.elapsedMs,
      networkJson: JSON.stringify(existingNetwork),
    });
  });
}

export async function closeChunk(
  chunkId: string,
  elapsedMs: number,
): Promise<void> {
  // A missing row means another tab already cleared the database, so there is nothing to close.
  await db.rrwebChunks.update(chunkId, { status: 'closed', elapsedMs });
}

export async function enforceRetention(tabId: string): Promise<void> {
  const chunks = await db.rrwebChunks
    .where('tabId')
    .equals(tabId)
    .sortBy('startTime');

  let totalSize = chunks.reduce((sum, chunk) => sum + chunk.sizeEstimate, 0);
  let remainingCount = chunks.length;
  const toDelete: string[] = [];

  for (const chunk of chunks) {
    if (remainingCount <= MAX_CHUNKS && totalSize <= MAX_TOTAL_SIZE_BYTES) {
      break;
    }

    if (chunk.status === 'open') continue;

    toDelete.push(chunk.id);
    remainingCount -= 1;
    totalSize -= chunk.sizeEstimate;
  }

  if (toDelete.length === 0) return;

  await db.transaction('rw', db.rrwebEvents, db.rrwebChunks, async () => {
    await db.rrwebEvents.where('chunkId').anyOf(toDelete).delete();
    await db.rrwebChunks.where('id').anyOf(toDelete).delete();
  });
}
